import type { ReactNode } from 'react'
import { NavLink, useMatches, useNavigate } from 'react-router-dom'

import { NAV_GROUPS, navItemHref, type NavItem } from '@/components/layout/navItems'
import { Button } from '@/components/ui/Button'
import { useLogout } from '@/lib/auth/session'
import { cn } from '@/lib/utils/cn'
import { useAuthStore } from '@/stores/authStore'
import { useVenueStore } from '@/stores/venueStore'

/**
 * 구장 관리자 웹 셸 — Figma `관리자 대시보드`(13989-46325) 실측.
 * 사이드바 224px / 톱바 56px / 본문 패딩 24·28.
 *
 * 메뉴는 이 파일이 아니라 `navItems.ts` 레지스트리가 소유한다 — 후속 화면이 붙을 때
 * 셸을 수정하지 않게 하기 위함이다(설계 §2-2(3)).
 *
 * Figma 의 `알림`(벨)·`구장 열람` 버튼은 대응 기능이 없어 렌더하지 않는다(설계 D5).
 */
interface RouteHandle {
  title?: string
}

/**
 * 화면 제목은 라우트가 `handle: { title }` 로 선언한다(설계 D7).
 * 가장 깊은 매치부터 거슬러 올라가며 첫 제목을 쓴다.
 */
function useRouteTitle(fallback: string): string {
  const matches = useMatches()

  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const handle = matches[index]?.handle as RouteHandle | undefined
    if (handle?.title) return handle.title
  }
  return fallback
}

function NavEntry({ venueId, item }: { venueId: string; item: NavItem }) {
  // 🚨 미구현 화면은 링크로 만들지 않는다 — 클릭하면 404 가 되기 때문이다(설계 D4).
  if (item.status === 'planned') {
    return (
      <span
        aria-disabled="true"
        className="flex cursor-not-allowed items-center justify-between rounded-lg px-3 py-2 text-sm text-slateish-400"
      >
        {item.label}
        <span className="rounded-full bg-slateish-200 px-2 py-0.5 text-[11px] text-slateish-600">
          준비 중
        </span>
      </span>
    )
  }

  return (
    <NavLink
      to={navItemHref(venueId, item)}
      end={item.path === ''}
      className={({ isActive }) =>
        cn(
          'block rounded-lg px-3 py-2 text-sm',
          isActive ? 'bg-brand-50 font-medium text-brand-500' : 'text-slateish-600'
        )
      }
    >
      {item.label}
    </NavLink>
  )
}

export function AppShell({ venueId, children }: { venueId: string; children: ReactNode }) {
  const navigate = useNavigate()
  // 🚨 서버 로그아웃 EP 를 먼저 기다린다 — 진행 중에는 버튼을 잠가 중복 호출을 막는다(설계 D7·R8).
  const [logout, isLoggingOut] = useLogout()
  const title = useRouteTitle('구장 관리')
  const venueName = useVenueStore((state) => state.venueName)
  const loginId = useAuthStore((state) => state.loginId)

  return (
    <div className="flex min-h-full">
      <aside className="hidden w-56 shrink-0 border-r border-slateish-200 bg-white lg:block">
        <div className="flex h-14 items-center gap-2 px-4">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-brand-500 text-xs font-bold text-white">
            FC
          </span>
          <span className="text-sm font-semibold text-ink-700">페이스콕 구장 관리</span>
        </div>

        <div className="mx-3 rounded-lg bg-brand-50 px-4 py-3">
          {/*
            구장명은 `VenueLayout` 이 내 구장 컨텍스트(#584 §1)에서 받아 넣는 표시용 미러다.
            조회 전이거나 실패했으면 `구장 #id` 로 폴백한다 — 이름을 못 얻었다고 화면을 막지 않는다(R5).
          */}
          <p className="truncate text-sm font-semibold text-brand-500">
            {venueName ?? `구장 #${venueId}`}
          </p>
          <button
            type="button"
            onClick={() => {
              navigate('/venues/select')
            }}
            className="mt-1 text-xs text-slateish-600 underline"
          >
            구장 변경
          </button>
          {/*
            #749 — "새 구장 등록"은 이미 최소 1개 구장을 관리 중인 상태에서만 제공한다(설계 §2 "진입
            경로"). `AppShell` 은 `VenueLayout` 안에서만 렌더되므로, 이 링크가 보인다는 사실 자체가
            그 조건을 보장한다 — 별도 가드가 필요 없다.
          */}
          <button
            type="button"
            onClick={() => {
              navigate('/venues/register')
            }}
            className="mt-1 block text-xs text-slateish-600 underline"
          >
            새 구장 등록
          </button>
        </div>

        <nav className="mt-4 px-3 pb-6">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="mb-3">
              <p className="px-2 pb-1 text-xs font-medium text-slateish-400">{group.label}</p>
              {group.items.map((item) => (
                <NavEntry key={`${group.label}/${item.path}`} venueId={venueId} item={item} />
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center justify-between border-b border-slateish-200 bg-white px-6">
          <h1 className="text-base font-semibold text-ink-700">{title}</h1>
          <div className="flex items-center gap-3">
            {loginId ? (
              <span className="flex items-center gap-2 text-sm text-slateish-600">
                <span
                  aria-hidden="true"
                  className="flex h-7 w-7 items-center justify-center rounded-full bg-slateish-200 text-xs font-medium text-slateish-600"
                >
                  {loginId.slice(0, 1).toUpperCase()}
                </span>
                {loginId}
              </span>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              disabled={isLoggingOut}
              onClick={() => {
                void logout()
              }}
            >
              {isLoggingOut ? '로그아웃 중' : '로그아웃'}
            </Button>
          </div>
        </header>
        <main className="min-w-0 flex-1 px-6 py-6">{children}</main>
      </div>
    </div>
  )
}
